import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Mail, Plus, Trash2, Send, Copy, Check, Loader2, Clock, Eye, CheckCircle2, Sparkles, Info, BellRing, Mic, Upload, FileDown } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { TopBar } from "@/pages/Dashboard";

const STATUS = {
  invited: { label: "Invited", icon: Clock, cls: "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]" },
  opened: { label: "Opened", icon: Eye, cls: "bg-amber-500/12 text-amber-600" },
  recorded: { label: "Recorded", icon: CheckCircle2, cls: "bg-emerald-500/12 text-emerald-600" },
};

const inputCls = "w-full h-11 px-3 rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes, CRLF.
function parseCSV(text) {
  const rows = [];
  let field = "", row = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field); rows.push(row); row = []; field = "";
    } else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((x) => (x || "").trim() !== ""));
}

const HEADER_ALIASES = {
  name: ["name", "full name", "fullname", "guest", "guest name", "person", "contact"],
  email: ["email", "e-mail", "email address", "mail", "e mail"],
  relationship: ["relationship", "relation", "relationship to", "role", "rel", "how they knew"],
};

function rowsFromCSV(text) {
  const grid = parseCSV(text);
  if (!grid.length) return [];
  const header = grid[0].map((h) => (h || "").trim().toLowerCase());
  const findIdx = (aliases) => header.findIndex((h) => aliases.includes(h));
  let ni = findIdx(HEADER_ALIASES.name);
  let ei = findIdx(HEADER_ALIASES.email);
  let ri = findIdx(HEADER_ALIASES.relationship);
  let dataRows;
  if (ni === -1 && ei === -1) {
    // No recognizable header — assume column order: name, email, relationship.
    ni = 0; ei = 1; ri = 2; dataRows = grid;
  } else {
    dataRows = grid.slice(1);
  }
  const cell = (r, idx) => (idx >= 0 && r[idx] != null ? String(r[idx]).trim() : "");
  const out = [];
  for (const r of dataRows) {
    const name = cell(r, ni), email = cell(r, ei), relationship = cell(r, ri);
    if (!name && !email) continue;
    out.push({ name: name.slice(0, 80), email, relationship: relationship.slice(0, 80) });
  }
  return out.slice(0, 500);
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.invited;
  const Icon = s.icon;
  return (
    <span data-testid={`invite-status-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.cls}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

export default function StoryInvites() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [rows, setRows] = useState([{ name: "", relationship: "", email: "" }]);
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const [autoNudge, setAutoNudge] = useState(true);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, inv] = await Promise.all([
        api.get(`/stories/${storyId}`),
        api.get(`/stories/${storyId}/invitations`),
      ]);
      setStory(s.data);
      setData(inv.data);
      setNote(inv.data.invite_note || "");
      setAutoNudge(inv.data.auto_nudge_enabled !== false);
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Story not found"); navigate("/dashboard"); }
    } finally { setLoading(false); }
  }, [storyId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Seed add-rows from setup interviewees that haven't been invited yet.
  useEffect(() => {
    if (!story || !data) return;
    const invitedEmails = new Set((data.invitations || []).map((i) => (i.email || "").toLowerCase()).filter(Boolean));
    const invitedNames = new Set((data.invitations || []).map((i) => (i.name || "").toLowerCase()).filter(Boolean));
    const pending = (story.interviewees || []).filter((p) => {
      const em = (p.email || "").toLowerCase();
      const nm = (p.name || "").toLowerCase();
      return (em && !invitedEmails.has(em)) || (!em && nm && !invitedNames.has(nm));
    }).map((p) => ({ name: p.name || "", relationship: p.relationship || "", email: p.email || "" }));
    if (pending.length) setRows(pending.concat([{ name: "", relationship: "", email: "" }]));
  }, [story, data]);

  const linkFor = (token) => `${window.location.origin}/si/${data?.slug || story?.slug}?i=${token}`;

  const copyLink = async (inv) => {
    try { await navigator.clipboard.writeText(linkFor(inv.invite_token)); setCopiedId(inv.invite_id); setTimeout(() => setCopiedId(""), 1600); toast.success("Personal link copied"); }
    catch { toast.error("Could not copy — please copy manually"); }
  };

  const setRow = (i, key, val) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [key]: val } : row));
  const addRow = () => setRows((r) => [...r, { name: "", relationship: "", email: "" }]);
  const removeRow = (i) => setRows((r) => r.length > 1 ? r.filter((_, idx) => idx !== i) : r);

  const downloadTemplate = () => {
    const csv = "name,email,relationship\nJane Doe,jane@example.com,Daughter\nJohn Smith,john@example.com,Nephew\nMaria (no email needed),,Neighbour\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "gathervoices-invite-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const onCSVFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("That file is a bit large — please keep CSVs under 1MB"); return; }
    const reader = new FileReader();
    reader.onerror = () => toast.error("Could not read that file");
    reader.onload = () => {
      let parsed;
      try { parsed = rowsFromCSV(String(reader.result || "")); }
      catch { toast.error("Could not parse that CSV"); return; }
      if (!parsed.length) { toast.error("No contacts found. Use columns: name, email, relationship."); return; }
      const invitedEmails = new Set((data?.invitations || []).map((i) => (i.email || "").toLowerCase()).filter(Boolean));
      const seen = new Set();
      const clean = [];
      let skipped = 0, badEmail = 0;
      for (const row of parsed) {
        const em = (row.email || "").toLowerCase();
        if (em) {
          if (invitedEmails.has(em) || seen.has(em)) { skipped++; continue; }
          if (!EMAIL_RE.test(row.email)) badEmail++;
          seen.add(em);
        }
        clean.push(row);
      }
      if (!clean.length) { toast.error(`All ${skipped} contact(s) were already invited`); return; }
      const existing = rows.filter((r) => r.name.trim() || r.email.trim());
      const merged = existing.concat(clean);
      setRows(merged.concat([{ name: "", relationship: "", email: "" }]));
      let msg = `Imported ${clean.length} ${clean.length === 1 ? "contact" : "contacts"} — review, then send`;
      if (skipped) msg += ` · skipped ${skipped} duplicate/already-invited`;
      if (badEmail) msg += ` · ${badEmail} email(s) look unusual`;
      toast.success(msg);
    };
    reader.readAsText(file);
  };

  const send = async (doSend) => {
    const valid = rows.filter((r) => r.name.trim() || r.email.trim());
    if (!valid.length) { toast.error("Add at least one person's name or email"); return; }
    setSending(true);
    try {
      await api.post(`/stories/${storyId}/invitations`, { invites: valid, personal_note: note, send: doSend });
      toast.success(doSend ? "Invitations sent" : "Invitees saved — copy their links below");
      setRows([{ name: "", relationship: "", email: "" }]);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invitations"); }
    finally { setSending(false); }
  };

  const resend = async (inv) => {
    setBusyId(inv.invite_id);
    try { await api.post(`/stories/${storyId}/invitations/${inv.invite_id}/resend`); toast.success(`Re-sent to ${inv.email}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); }
    finally { setBusyId(""); }
  };

  const nudge = async (inv) => {
    setBusyId(inv.invite_id);
    try { await api.post(`/stories/${storyId}/invitations/${inv.invite_id}/reminder`); toast.success(`Gentle nudge sent to ${inv.email}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send reminder"); }
    finally { setBusyId(""); }
  };

  const toggleAutoNudge = async (val) => {
    setAutoNudge(val);
    try { await api.patch(`/stories/${storyId}`, { auto_nudge_enabled: val }); toast.success(val ? "Automatic reminders on" : "Automatic reminders off"); }
    catch (e) { setAutoNudge(!val); toast.error("Could not update setting"); }
  };

  const remove = async (inv) => {
    setBusyId(inv.invite_id);
    try { await api.delete(`/stories/${storyId}/invitations/${inv.invite_id}`); toast.success("Removed"); await load(); }
    catch { toast.error("Could not remove"); }
    finally { setBusyId(""); }
  };

  if (loading) return <div className="min-h-screen"><TopBar /><div className="max-w-4xl mx-auto px-4 sm:px-6 py-12"><div className="h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" /></div></div>;
  if (!story) return null;

  const invites = data?.invitations || [];
  const counts = data?.counts || { invited: 0, opened: 0, recorded: 0, total: 0 };
  const emailOk = data?.email_configured;

  return (
    <div className="min-h-screen" data-testid="story-invites">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <button onClick={() => navigate(`/stories/${storyId}`)} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:underline"><ArrowLeft size={15} /> Back to story</button>

        <div className="mt-4 flex items-center gap-3">
          <div className="h-11 w-11 grid place-items-center rounded-xl bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))]"><Users size={20} /></div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold">Invite the family</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Send each relative a personal interview link for {story.title}.</p>
          </div>
        </div>

        {/* counts */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[["invited", "Invited"], ["opened", "Opened"], ["recorded", "Recorded"]].map(([k, label]) => (
            <div key={k} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`invite-count-${k}`}>
              <div className="font-display text-2xl font-semibold tabular-nums">{counts[k] || 0}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
            </div>
          ))}
        </div>

        {!emailOk && (
          <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700" data-testid="invite-email-banner">
            <Info size={17} className="shrink-0 mt-0.5" />
            <div>Email delivery isn't configured yet. You can still <strong>copy each personal link</strong> below and share it however you like. A Super Admin can add a Resend key in Settings to send emails automatically.</div>
          </div>
        )}

        {/* auto reminders */}
        <div className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="invite-auto-nudge">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-9 w-9 shrink-0 grid place-items-center rounded-lg bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))]"><BellRing size={17} /></div>
            <div className="min-w-0">
              <div className="font-semibold text-sm">Automatic reminders</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">We'll gently nudge anyone who opened their link but hasn't recorded — at most twice, a few days apart. No nagging.</p>
            </div>
          </div>
          <Switch checked={autoNudge} onCheckedChange={toggleAutoNudge} data-testid="invite-auto-nudge-switch" aria-label="Toggle automatic reminders" />
        </div>

        {/* add form */}
        <section className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6" data-testid="invite-add-form">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Sparkles size={17} className="text-[hsl(var(--accent))]" /> Who would you like to hear from?</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => fileRef.current?.click()} data-testid="invite-import-csv"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-medium hover:bg-[hsl(var(--muted))]">
                <Upload size={15} /> Import CSV
              </button>
              <button onClick={downloadTemplate} data-testid="invite-csv-template"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                <FileDown size={15} /> Template
              </button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onCSVFile} className="hidden" data-testid="invite-csv-input" />
            </div>
          </div>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Add people one at a time, or import a CSV with columns <span className="font-mono text-[hsl(var(--foreground))]">name, email, relationship</span>. Email is optional — you can still copy a personal link.</p>
          <div className="mt-4 space-y-3">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <input data-testid={`invite-name-${i}`} value={row.name} onChange={(e) => setRow(i, "name", e.target.value)} placeholder="Name" className={inputCls} />
                <input data-testid={`invite-rel-${i}`} value={row.relationship} onChange={(e) => setRow(i, "relationship", e.target.value)} placeholder="Relationship (optional)" className={inputCls} />
                <input data-testid={`invite-email-${i}`} value={row.email} onChange={(e) => setRow(i, "email", e.target.value)} placeholder="Email (optional)" className={inputCls} />
                <button onClick={() => removeRow(i)} aria-label="Remove row" className="h-11 w-11 grid place-items-center rounded-xl border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]" data-testid={`invite-remove-row-${i}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <button onClick={addRow} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--accent))]" data-testid="invite-add-row"><Plus size={15} /> Add another person</button>

          <div className="mt-5">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">A personal note (optional)</label>
            <textarea data-testid="invite-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
              placeholder={`We're putting together a keepsake documentary about ${story.honoree_name}, and your memories would mean the world…`}
              className="w-full px-3 py-2.5 rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button data-testid="invite-send-button" onClick={() => send(true)} disabled={sending}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 disabled:opacity-60">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send invitations
            </button>
            <button data-testid="invite-save-links-button" onClick={() => send(false)} disabled={sending}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] disabled:opacity-60">
              <Copy size={16} /> Just create links
            </button>
          </div>
        </section>

        {/* tracker */}
        <section className="mt-8" data-testid="invite-tracker">
          <h2 className="font-display text-lg font-semibold">Who's been invited</h2>
          {invites.length === 0 ? (
            <div className="mt-3 rounded-2xl border border-dashed border-[hsl(var(--border))] p-8 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="invite-empty">
              No one invited yet. Add family above and we'll track who opens their link and records.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {invites.map((inv) => (
                <div key={inv.invite_id} data-testid={`invite-row-${inv.invite_id}`} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{inv.name || inv.email || "Guest"}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                      {inv.relationship ? `${inv.relationship}` : ""}{inv.relationship && inv.email ? " · " : ""}{inv.email || (inv.name ? "" : "")}
                      {!inv.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> no email — share link</span>}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[hsl(var(--muted-foreground))]" data-testid={`invite-analytics-${inv.invite_id}`}>
                      {inv.last_opened_at && <span className="inline-flex items-center gap-1"><Eye size={11} /> opened {timeAgo(inv.last_opened_at)}</span>}
                      {inv.chapters_recorded > 0 && <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><Mic size={11} /> {inv.chapters_recorded} chapter{inv.chapters_recorded !== 1 ? "s" : ""} recorded</span>}
                      {!inv.last_opened_at && !inv.chapters_recorded && <span className="opacity-70">not opened yet</span>}
                    </div>
                  </div>
                  <StatusBadge status={inv.status} />
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`invite-copy-${inv.invite_id}`} onClick={() => copyLink(inv)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-medium hover:bg-[hsl(var(--muted))]">
                      {copiedId === inv.invite_id ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Link</>}
                    </button>
                    {inv.email && inv.status === "opened" && (
                      <button data-testid={`invite-nudge-${inv.invite_id}`} onClick={() => nudge(inv)} disabled={busyId === inv.invite_id} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[hsl(var(--accent))]/40 bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 disabled:opacity-60">
                        {busyId === inv.invite_id ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} Nudge
                      </button>
                    )}
                    {inv.email && inv.status !== "opened" && (
                      <button data-testid={`invite-resend-${inv.invite_id}`} onClick={() => resend(inv)} disabled={busyId === inv.invite_id} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-medium hover:bg-[hsl(var(--muted))] disabled:opacity-60">
                        {busyId === inv.invite_id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Resend
                      </button>
                    )}
                    <button data-testid={`invite-delete-${inv.invite_id}`} onClick={() => remove(inv)} disabled={busyId === inv.invite_id} aria-label="Remove invitee" className="h-9 w-9 grid place-items-center rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))] disabled:opacity-60">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
